/*
 * Pure PII-scrub logic extracted from resumeSanitizer.js so it can be
 * unit-tested from Node/Jest without pulling in pdfjs-dist or mammoth.
 *
 * `scrubText(input) -> string` is the entry point. Everything here is a
 * pure function of its input — no I/O, no worker, no DOM.
 */

// Section headings that mark the end of the résumé header. Case-insensitive.
// Covers EN, PT-BR, ES-419 (Latin American Spanish), and FR-CA (Canadian
// French). Localized keywords use accented characters as they appear in
// real résumés; matching itself is accent-preserving (résumé authors keep
// accents so we rely on them for high-signal matches — HEADING_RE below is
// applied to the raw line, no fold-to-ASCII pass is performed).
const SECTION_HEADING_PATTERNS = [
  // English
  "professional experience", "work experience", "employment history",
  "employment", "experience", "professional summary", "career summary",
  "summary", "profile", "objective", "career objective",
  // Profile/summary variants. "Personal profile", "personal statement" and
  // "about me" are left out on purpose: those sections often hold personal
  // details, so they stay inside the stripped header block.
  "professional profile", "career profile", "profile summary",
  "executive summary", "executive profile", "summary of qualifications",
  "professional overview", "career overview", "technical profile", "technical summary",
  "skills", "technical skills", "core skills", "core competencies", "competencies",
  "education", "academic background", "qualifications", "academic",
  "certifications", "licenses", "licences", "certificates",
  "projects", "key projects", "selected projects",
  "achievements", "key achievements", "awards", "honors", "honours",
  "languages", "publications", "volunteer", "volunteering",
  // Portuguese
  "experiência profissional", "experiência", "histórico profissional",
  "resumo profissional", "perfil profissional", "perfil", "objetivo", "objetivo profissional",
  "competências", "competências técnicas", "habilidades",
  "formação", "formação acadêmica", "educação", "qualificações",
  "certificações", "certificados", "licenças",
  "projetos", "principais projetos",
  "conquistas", "prêmios", "reconhecimentos",
  "idiomas", "publicações", "voluntariado",
  // Spanish (Latin American)
  "experiencia profesional", "experiencia laboral", "historial laboral",
  "trayectoria profesional", "experiencia",
  "resumen profesional", "resumen ejecutivo", "resumen",
  "perfil profesional", "objetivo profesional",
  "habilidades", "habilidades técnicas", "competencias principales",
  "competencias", "aptitudes", "conocimientos",
  "formación académica", "formación", "educación", "estudios",
  "titulación", "titulaciones",
  "certificaciones", "certificaciones y capacitación",
  "logros", "logros clave", "premios", "reconocimientos", "distinciones",
  "idiomas", "publicaciones", "voluntariado",
  "sistemas y herramientas", "herramientas",
  // French (Canadian)
  "expérience professionnelle", "expériences professionnelles",
  "expérience", "expériences", "parcours professionnel", "historique professionnel",
  "résumé professionnel", "résumé", "profil professionnel", "profil",
  "objectif", "objectif professionnel", "objectif de carrière",
  "compétences", "compétences principales", "compétences techniques",
  "aptitudes", "savoir-faire", "connaissances",
  "formation", "formation académique", "éducation", "scolarité", "études",
  "diplômes", "qualifications",
  "certifications", "certifications et formation", "attestations",
  "projets", "projets clés", "projets principaux",
  "réalisations", "prix", "distinctions", "récompenses", "honneurs",
  "langues", "publications", "bénévolat",
  "systèmes et outils", "outils",
];

export const HEADING_RE = new RegExp(
  "^\\s*(" +
    SECTION_HEADING_PATTERNS.map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|") +
    ")\\s*[:\\-–—]?\\s*$",
  "i"
);

// References/letter-of-recommendation section start — everything from the
// heading to end-of-document is removed. Covers EN, PT-BR, ES-419, FR-CA.
export const REFERENCES_RE = /^\s*(references|referências|referencias|références|référencias|professional references|referências profissionais|referencias profesionales|références professionnelles|letters? of recommendation|letters? of reference|carta de recomendação|cartas de recomendação|carta de referência|cartas de referência|cartas de recomendación|cartas de referencia|lettres? de recommandation|lettres? de référence)\s*[:\-–—]?\s*$/i;

// "Personal Details" sections (fix 1, RESUME_SCANNER_GAPS.md, 2026-10-28): common at
// the end of international résumés. Everything from this heading to the next
// recognised section heading is removed. Covers EN, PT-BR, ES-419, FR-CA.
export const PERSONAL_SECTION_RE = /^\s*(personal details|personal information|personal data|personal info|personal particulars|dados pessoais|informações pessoais|informacoes pessoais|datos personales|información personal|informacion personal|renseignements personnels|informations personnelles|données personnelles|donnees personnelles)\s*[:\-–—]?\s*$/i;

// PII regexes ---------------------------------------------------------------

export const EMAIL_RE = /[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}/gi;

export const URL_RE = /(?:https?:\/\/|www\.)\S+/gi;
export const SOCIAL_HANDLE_RE = /\b(linkedin\.com|github\.com|gitlab\.com|behance\.net|dribbble\.com|medium\.com|twitter\.com|x\.com|facebook\.com|instagram\.com|tiktok\.com|youtube\.com|t\.me|telegram\.me|wa\.me)\/\S+/gi;

// Phones — Brazilian mobile/landline + international +CC formats.
// Includes phone-line keywords in EN, PT-BR, ES-419, FR-CA.
export const PHONE_KEYWORDS_RE = /(?:phone|tel|telefone|teléfono|téléphone|mobile|móvil|movil|celular|cellulaire|portable|whatsapp|whats)\s*[:.\-]?\s*([+\d][\d\s().\-]{7,}\d)/gi;
export const RAW_PHONE_RE = /(\+?\d[\d\s().\-]{8,}\d)/g;

// Government IDs — Brazilian and international patterns.
export const CPF_RE = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g;
export const CNPJ_RE = /\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/g;
export const RG_RE = /\b\d{1,2}\.?\d{3}\.?\d{3}-?\d{1,2}\b/g;
export const SSN_RE = /\b\d{3}-\d{2}-\d{4}\b/g;
export const SIN_RE = /\b\d{3}[\s\-]?\d{3}[\s\-]?\d{3}\b/g;
export const PASSPORT_RE = /(?:passport|passaporte|pasaporte|passeport)\s*(?:no\.?|number|nº|n°|:)?\s*[A-Z0-9]{6,10}/gi;

// National IDs (fix 3, RESUME_SCANNER_GAPS.md, 2026-10-28). Each format is matched in
// full so no partial number (or birth year) is left behind.
export const NATIONAL_ID_RES = [
  /\b784-?\d{4}-?\d{7}-?\d\b/g, // UAE Emirates ID (contains the birth year)
  /\b\d{5}-\d{7}-\d\b/g, // Pakistan CNIC
  /\b\d{2}-\d{7}-\d\b/g, // Philippines SSS
  /\b[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]\d\b/g, // Mexico CURP
  /\b[A-Z]{5}\d{4}[A-Z]\b/g, // India PAN
  /\b[A-CEGHJ-PR-TW-Z]{2}\s?\d{2}\s?\d{2}\s?\d{2}\s?[A-D]\b/g, // UK National Insurance number
  /\b[STFGM]\d{7}[A-Z]\b/g, // Singapore NRIC / FIN
  /\b[XYZ]-?\d{7}-?[A-Z]\b/g, // Spain NIE
  /\b\d{8}-?[A-Z]\b/g, // Spain DNI
  /\b\d{1,2}\.\d{3}\.\d{3}-[\dkK]\b/g, // Chile RUT
];
// India Aadhaar: exactly three groups of 4 digits. Checked in code, not with a
// lookbehind, which older Safari can't parse (it would break the whole scrubber).
export const AADHAAR_RE = /\b\d{4}\s\d{4}\s\d{4}\b/g;

function exactlyThreeGroups(match, offset, whole) {
  const before = whole.slice(Math.max(0, offset - 2), offset);
  const after = whole.slice(offset + match.length, offset + match.length + 2);
  return !/\d[ -]?$/.test(before) && !/^[ -]?\d/.test(after);
}

// Certificate, licence and permit numbers: only the number goes, the certificate's name
// stays ("Forklift Operator Certificate FKL-88421" -> "Forklift Operator Certificate").
// A bare year ("Certificate 2019") is kept. Batch ba_cf0168a19089, 2026-10-28.
export const CERT_NUMBER_RE = /((?:^|[\s(,;:])(?:certificate|certification|cert\.?|permit|licen[cs]e|licen[cç]a|licencia|permis|certificado|certificat)\b\s*(?:no\.?|number|num\.?|nº|n°|#)?\s*[:#]?\s*)(?!(?:19|20)\d{2}\b)(?=[A-Z0-9-]*\d)[A-Z0-9][A-Z0-9-]{3,}/gi;

// Any number after an ID label: "National ID: …", "NIN …", "ID No. …".
// The label must start the line or follow a space or punctuation (JS \b doesn't see
// accented letters, so "nie" inside "Ingénieur" matched), end at a word boundary, and the
// value must contain a digit.
export const LABELLED_NATIONAL_ID_RE = /(^|[\s(,;:])(?:national id(?:entity)?(?: card)?(?: number| no\.?)?|id (?:number|no\.?|card)|identity (?:number|card)|nin|ni number|national insurance(?: number)?|nric|cnic|curp|pan(?: number| no\.?)?|aadhaar|dni|nie|rut|emirates id|sss(?: number| no\.?)?|cédula|cedula|carte d'identité|documento de identidad|bilhete de identidade)\b\s*[:#\-]?\s*(?=[A-Z0-9.\- ]*\d)[A-Z0-9][A-Z0-9.\- ]{4,}[A-Z0-9]/gi;

// Date of birth patterns. Covers EN, PT-BR, ES-419, FR-CA.
export const DOB_KEYWORDS_RE = /\b(date of birth|dob|birthdate|birth date|born on|nascimento|data de nascimento|nascido em|nascida em|fecha de nacimiento|fecha de nac\.?|nacido el|nacida el|date de naissance|né(?:\(?e\)?)? le|née le)\b[:\-\s]*[^\n]{0,40}/gi;

// Fix 2 (RESUME_SCANNER_GAPS.md, 2026-10-28): personal details written inline.
// "Born 14 March 1990", "born in 1990", "Born: 1990-03-14".
const MONTHS = "jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?";
export const BORN_RE = new RegExp(
  "\\bborn\\s*(?:on|in)?\\s*[:\\-]?\\s*(?:\\d{1,2}(?:st|nd|rd|th)?\\s+(?:" + MONTHS + ")\\.?,?\\s+\\d{4}|(?:" + MONTHS + ")\\.?\\s+\\d{1,2}(?:st|nd|rd|th)?,?\\s+\\d{4}|\\d{1,4}[/.\\-]\\d{1,2}[/.\\-]\\d{1,4}|(?:19|20)\\d{2})",
  "gi"
);
// "34 years old", "aged 34".
export const AGE_INLINE_RE = /\b(?:\d{2}\s+years?\s+old|aged\s+\d{2})\b/gi;
// "Brazilian citizen", "Canadian national" (demonym-shaped words only, not "Senior citizen").
export const CITIZEN_INLINE_RE = /\b[A-Z][a-z]+(?:an|ian|ese|ish|ch|i)\s+(?:citizen|national|citizenship)\b/g;
// A line that is only a marital status: "Married", "Single, two children", "Married with two kids".
// Marital status or children inside a sentence (batch ba_fd41facffaea, 2026-10-28:
// "Outside work, I was and am married with two children." stayed in). "Single" is left
// out here: inline it usually means something else ("single point of contact").
// The word end is checked with a lookahead, since JS \b doesn't see accented letters.
// A bare status word counts only when it reads as a status: after "I am" / "happily" and
// the like (kept, group 2), before children, at the end of a clause, or before "to". Not
// as a verb with an object: "married the two systems" (privacy audit, 2026-10-28).
const MARITAL_STATUS = "(?:married|divorced|widowed|casad[oa]|divorciad[oa]|viúv[oa]|viud[oa]|mariée?|divorcée?|veuf|veuve)";
const MARITAL_KIDS = "(?:\\s+(?:with|and|com|con|avec)\\s+(?:\\S+\\s+){0,3}?(?:children|kids|child|sons?|daughters?|filhos?|filhas?|hijos?|hijas?|enfants?))";
const MARITAL_END = "(?=[\\s,.;:!?)]|$)";
export const MARITAL_INLINE_RE = new RegExp(
  "(^|[\\s(,;:])(?:" +
    "((?:am|is|was|are|i'm|i’m|happily|recently|newly|now|currently|sou|soy|estoy|suis)\\s+)" + MARITAL_STATUS + MARITAL_KIDS + "?" + MARITAL_END +
    "|" + MARITAL_STATUS + MARITAL_KIDS + MARITAL_END +
    "|" + MARITAL_STATUS + "(?=[ \\t]*(?:[,.;:!?)\\n]|$)|\\s+to[\\s,.;:!?)]|\\s+to$)" +
    "|(?:father|mother|parent|pai|mãe|padre|madre|père|mère) (?:of|de) (?:\\S+\\s+){0,2}?(?:children|kids|filhos|hijos|enfants)" + MARITAL_END +
  ")",
  "gim",
);
export const MARITAL_LINE_RE = /^\s*(?:married|single|divorced|widowed|separated|engaged|casado|casada|solteiro|solteira|soltero|soltera|divorciado|divorciada|marié|mariée|célibataire|divorcé|divorcée)\b(?:\s*(?:,|with|and|com|con|avec)\s*[^\n]{0,40})?\s*$/i;
// Personal labels matched with or without a colon ("Marital status Married", "Religion Catholic").
// Without a colon only a short value (1-3 words) counts, so "Citizenship and Immigration Canada" stays.
export const PERSONAL_LABEL_RE = /^\s*(religion|religião|religión|age|idade|edad|âge|sex|citizenship|cidadania|ciudadanía|citoyenneté|civil status|marital status|estado civil|état civil|birth date|birthplace|place of birth|naturalidade|lugar de nacimiento|lieu de naissance|nationality|nacionalidad|nacionalidade|nationalité|gender|género|sexo|sexe|genre|dependents|dependants|children)\s*(?:[:\-–—]\s*.*|\s+(?!and\b|&|of\b)\S+(?:\s+\S+){0,2})$/i;

// Fix 4 (RESUME_SCANNER_GAPS.md, 2026-10-28): personal sites without http(s):// or www.
// Link-in-bio and free-hosting domains are always personal; any other plain domain
// is removed when it is the whole line or follows a website/portfolio label, so an
// employer named in a sentence ("agent at Booking.com") stays.
export const LINK_IN_BIO_RE = /\b(?:[a-z0-9-]+\.)*(?:linktr\.ee|beacons\.ai|bio\.link|carrd\.co|about\.me|github\.io|gitlab\.io|netlify\.app|vercel\.app|wixsite\.com|squarespace\.com|wordpress\.com|notion\.site|behance\.net|dribbble\.com)(?:\/\S*)?/gi;
// Colon only: "Développeur web — TechCorp" is a job title, not a website.
export const SITE_LABEL_RE = /(^|[\s(,;])(website|web site|site|portfolio|portfólio|portafolio|portefeuille|blog|homepage|web)\s*:\s*\S+/gi;
export const DOMAIN_ONLY_LINE_RE = /^\s*[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}(?:\/\S*)?\s*$/i;

// Contact-label lines (Name:, Address:, Marital status:, …).
// NOTE: no `g` flag — `.test()` is called per-line and the global flag
// makes RegExp state persist between calls, producing false negatives.
// Covers EN, PT-BR, ES-419, FR-CA.
export const CONTACT_LABEL_RE = /^\s*(name|nombre|nome|nom|prénom|address|dirección|endereço|adresse|residência|residence|city|ciudad|cidade|ville|state|estado|province|zip|cep|postal code|code postal|country|país|pays|nationality|nacionalidad|nacionalidade|nationalité|gender|género|sexo|sexe|genre|marital|estado civil|état civil|date of birth|fecha de nacimiento|data de nascimento|date de naissance|email|correo|correo electrónico|courriel|e-mail|phone|teléfono|telefone|téléphone|mobile|móvil|celular|cellulaire|portable|linkedin|linked ?in)\s*[:\-–—]\s*.*$/im;

/**
 * Strip PII from raw résumé text.
 *
 * Steps:
 *   1. Split into lines and drop everything before the first recognised
 *      section heading (résumé header block: name/email/phone/address/photo
 *      references).
 *   2. Detect a "References" or "Letters of Recommendation" heading and
 *      drop everything from that point on.
 *   3. Walk the surviving lines and blank out any direct identifier via
 *      regex so identifiers outside the header (footers, prose) are
 *      removed too.
 *
 * @param {string} input
 * @returns {string}
 */
export function scrubText(input) {
  const rawLines = String(input || "").split(/\r?\n/);

  // Step 1: strip header until first section heading.
  const firstSectionIdx = rawLines.findIndex((ln) => HEADING_RE.test(ln.trim()));
  const startIdx = firstSectionIdx >= 0 ? firstSectionIdx : 0;

  // Step 2: cut off References section.
  const refsRelative = rawLines
    .slice(startIdx)
    .findIndex((ln) => REFERENCES_RE.test(ln.trim()));
  const endIdx = refsRelative >= 0 ? startIdx + refsRelative : rawLines.length;

  const bodyLines = rawLines.slice(startIdx, endIdx);

  // Step 3: drop "Personal Details" sections up to the next section heading.
  const keptLines = [];
  let inPersonal = false;
  for (const ln of bodyLines) {
    if (PERSONAL_SECTION_RE.test(ln.trim())) {
      inPersonal = true;
      continue;
    }
    if (inPersonal && HEADING_RE.test(ln.trim())) inPersonal = false;
    if (!inPersonal) keptLines.push(ln);
  }

  // Step 4: global scrub.
  const cleanedLines = keptLines.map((line) => scrubLine(line));

  // Collapse 3+ consecutive blank lines.
  const compacted = [];
  let blankRun = 0;
  for (const ln of cleanedLines) {
    if (!ln.trim()) {
      blankRun += 1;
      if (blankRun <= 1) compacted.push("");
    } else {
      blankRun = 0;
      compacted.push(ln);
    }
  }
  return compacted.join("\n").trim();
}

export function scrubLine(line) {
  let out = line;
  if (CONTACT_LABEL_RE.test(out)) return "";
  if (PERSONAL_LABEL_RE.test(out) || MARITAL_LINE_RE.test(out) || DOMAIN_ONLY_LINE_RE.test(out)) return "";

  out = out.replace(DOB_KEYWORDS_RE, "");
  out = out.replace(BORN_RE, "");
  out = out.replace(MARITAL_INLINE_RE, "$1$2");
  out = out.replace(AGE_INLINE_RE, "");
  out = out.replace(CITIZEN_INLINE_RE, "");
  out = out.replace(SITE_LABEL_RE, "$1");
  out = out.replace(LINK_IN_BIO_RE, "");
  out = out.replace(EMAIL_RE, "");
  out = out.replace(SOCIAL_HANDLE_RE, "");
  out = out.replace(URL_RE, "");

  // Government IDs — order matters (longer patterns first). Labelled and
  // dedicated national formats go first so no partial number is left behind.
  out = out.replace(LABELLED_NATIONAL_ID_RE, "$1");
  out = out.replace(CERT_NUMBER_RE, "$1");
  for (const re of NATIONAL_ID_RES) out = out.replace(re, "");
  out = out.replace(AADHAAR_RE, (m, offset, whole) => (exactlyThreeGroups(m, offset, whole) ? "" : m));
  out = out.replace(CNPJ_RE, "");
  out = out.replace(CPF_RE, "");
  out = out.replace(SSN_RE, "");
  out = out.replace(SIN_RE, "");
  out = out.replace(PASSPORT_RE, "");
  out = out.replace(RG_RE, "");

  out = out.replace(PHONE_KEYWORDS_RE, "");
  out = out.replace(RAW_PHONE_RE, (match) => {
    const digits = match.replace(/\D/g, "");
    if (digits.length < 8) return match;
    // Avoid stripping calendar-year ranges like "2011 - 2015" or
    // "2011 – 2015" that are structurally similar to short phones. Real
    // phone numbers have parentheses, a country-code +, or 10+ digits.
    const hasPhoneShape = /[+()]/.test(match) || digits.length >= 10;
    if (!hasPhoneShape) {
      const yearRange = /^\s*(?:19|20)\d{2}\s*[-–—/]\s*(?:19|20)\d{2}\s*$/;
      if (yearRange.test(match)) return match;
    }
    return "";
  });

  return out.replace(/[ \t]{2,}/g, " ").replace(/\s+([,.;:])/g, "$1").trim();
}
